import type { Messages } from '../index';
import { DOCUMENT_CATEGORY_LABELS } from './document-category-labels';

/**
 * en-US assistant notice strings (IMM-307.3). Shape is constrained by
 * `Messages['notices']` (derived from the canonical German dictionary), so any
 * drift between `de` and `en` is a `tsc` error.
 */
export const notices: Messages['notices'] = {
  assistant: {
    run: {
      'stopped-limit': 'This request grew too large and was stopped. Please try asking something narrower.',
      failed: 'Something went wrong. Please try again.',
    },
    'page-read': {
      'not-supported': 'I can only read pages on ImmoScout24 for now. Open the page there and ask again.',
    },
  },
  messenger: {
    'agent-question': {
      // The ticket's product TL;DR (OPS-87): the message opens with what the ticket is, before the engineering.
      brief: '📋 {ticket}: {brief}',
      heading: '🤖 {lane} on {ticket} needs a decision:',
      option: '{number}. {text}',
      'option-recommended': '{number}. {text} (recommended)',
      'ticket-link': 'Ticket: {link}',
      'reply-with-options': 'Reply with the number of an option or with your own answer.',
      'reply-without-options': 'Just reply with your decision.',
      link: 'Also on the fleet page: {link}',
      chosen: 'Chosen: {key}',
      'answered-elsewhere': 'Answered in the admin app: {answer}',
      'answered-in-chat': 'Recorded in the chat: {answer}',
      withdrawn: 'No longer open.',
    },
    // The fleet assistant's fixed replies (IMM-1420); free replies go out unchanged.
    'fleet-assistant': {
      'asks-for-ticket': 'Which ticket do you mean? These are open right now: {tickets}',
      'asks-for-ticket-none': 'Which ticket do you mean? No job is open right now.',
      refused:
        'I cannot do that for you. I can answer anything about the fleet and record answers to open questions – everything else is on the fleet page.',
      failed: 'I cannot answer right now. Try again in a moment – the answer buttons still work.',
      'answer-recorded': 'Recorded: {ticket} is now “{answer}”.',
      'already-answered': '{ticket} is already answered: “{answer}” ({name}). I changed nothing.',
    },
    // One-way fleet notices (OPS-62): no reply is expected.
    'fleet-job-parked': {
      heading: '🚨 {lane} parked {ticket} and needs a human.',
      'ticket-link': 'Ticket: {link}',
      reason: 'Reason: {reason}',
      link: 'Fleet page: {link}',
    },
    'fleet-worker-silent': {
      job: '🛑 Worker {worker} on host {host} stopped reporting while on {ticket}.',
      released: 'Its job was released, so another worker can take the ticket.',
      link: 'Fleet page: {link}',
    },
    'fleet-release-blocked': {
      heading: '🚧 Release {version} is blocked (reported by {machine}).',
      manual: 'The deploy did not run. Run /feature-deploy by hand once the cause is fixed.',
    },
    // A question left unanswered for its window comes back later (OPS-79).
    'still-open': '⏳ Still open since {time}:',
    // The reply to `/pending` (OPS-79).
    pending: {
      heading: 'Open questions ({count}), oldest first:',
      item: '{number}. {ticket}: {headline}',
      'item-open': '{number}. {ticket}: {headline} (your next reply goes here)',
      footer: 'Quote a question’s message to answer it. The others come back in turn.',
      empty: 'No open questions.',
    },
    'quoted-not-open': 'That question is no longer open. /pending lists the open ones.',
  },
  // Rendered only by `I18nTelegramCopy` — never in code.
  telegram: {
    'assistant-moved':
      'The immoOS assistant has moved. From now on, write to it here: @immoos_bot – and reconnect your account in your immoOS profile if needed.',
    'unknown-sender':
      'Hello! You have reached the immoOS assistant for estate agents on Telegram. You are not registered as an agent with immoOS yet. Please contact our sales team: info@immoos.ai',
    link: {
      welcome: 'Your Telegram account is now connected to immoOS, {firstName}. Just ask me whatever you want to know.',
      'welcome-without-name':
        'Your Telegram account is now connected to immoOS. Just ask me whatever you want to know.',
      'account-taken': 'This Telegram account is already connected to another immoOS account.',
    },
  },
  whatsapp: {
    link: {
      welcome: "Your number is now connected to immoOS, {firstName}. Just ask me what you'd like to know.",
      'welcome-without-name': "Your number is now connected to immoOS. Just ask me what you'd like to know.",
      'invalid-code': 'That code is wrong or has expired. Get a new one from your immoOS profile.',
      'number-taken': 'This WhatsApp number is already connected to a different immoOS account.',
    },
    'media-not-supported': 'I can only read text messages right now. Please write to me as text.',
    'unknown-sender':
      'Hello! You have reached the number of the immoOS assistant for estate agents. You are not registered as an agent with immoOS yet. Please contact our sales team: info@immoos.ai',
    confirm: {
      affirmative: 'Yes',
      question: 'Should I send this as is? Reply "Yes" and I\'ll send it.',
      'document-request':
        'I would send this document request to the owner:\nProperty: {property}\nDocuments: {categories}\nNote: {note}',
      'document-request-no-note':
        'I would send this document request to the owner:\nProperty: {property}\nDocuments: {categories}',
      'viewing-invitation':
        'I would send this viewing invitation by e-mail:\nProspect: {prospect}\nWhen: {date}, {start}–{end}\nNote: {hint}',
      'viewing-invitation-no-hint':
        'I would send this viewing invitation by e-mail:\nProspect: {prospect}\nWhen: {date}, {start}–{end}',
      category: DOCUMENT_CATEGORY_LABELS,
    },
  },
};
